import React, { useState, useEffect } from "react";
import { Modal, FormActions, Input, IconsLarge } from "../../shared";
import { costCentersApi } from "../../../services";
import type { CostCenterAttributes } from "../../../types";

interface CreateCostCenterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: number;
  editingCostCenter?: CostCenterAttributes | null;
}

export const CreateCostCenterModal: React.FC<CreateCostCenterModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  editingCostCenter,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");

  useEffect(() => {
    if (editingCostCenter) {
      setName(editingCostCenter.name);
    } else {
      setName("");
    }
    setError(null);
  }, [editingCostCenter, isOpen]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Nome do centro de custo é obrigatório");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingCostCenter) {
        const response = await costCentersApi.update(editingCostCenter.id, {
          name: name.trim(),
        });
        if (response.success) {
          onSuccess();
          onClose();
        } else {
          setError(response.error ?? "Falha ao atualizar centro de custo");
        }
      } else {
        const response = await costCentersApi.create({
          name: name.trim(),
          companyId,
        });
        if (response.success) {
          onSuccess();
          onClose();
        } else {
          setError(response.error ?? "Falha ao criar centro de custo");
        }
      }
    } catch {
      setError(
        editingCostCenter
          ? "Falha ao atualizar centro de custo"
          : "Falha ao criar centro de custo"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName("");
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editingCostCenter ? "Editar Centro de Custo" : "Novo Centro de Custo"}
      icon={<IconsLarge.CostCenter />}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 mb-6">
          <span className="text-xl">⚠️</span>
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      <div className="space-y-6">
        <Input
          label="Nome do centro de custo"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
            if (e.key === "Escape") handleClose();
          }}
          placeholder="Ex: Marketing, Vendas, Operações..."
          disabled={loading}
          required
        />

        <FormActions
          onCancel={handleClose}
          onSubmit={handleSubmit}
          loading={loading}
          isEditing={!!editingCostCenter}
          submitType="button"
          submitText={
            editingCostCenter ? "Atualizar centro de custo" : "Criar centro de custo"
          }
        />
      </div>
    </Modal>
  );
};
